<template>
  <div class="flex">
    <span class="flex-1 flex justify-center items-center text-center bg-gray-200 rounded-l-xl gap-3 py-3">
      <Checkbox v-if="row.optional" :model-value="isEnabled" @update:model-value="updateEnabled" />
      {{ row.label }}
    </span>
    <span v-if="hasExcluded" class="flex-1 flex justify-center align-center gap-1 py-3 text-primary">
      {{ excludedRatio }}% <span class="text-sm">({{ row.excluded.total }})
      </span>
    </span>
    <span
      v-if="hasExcluded"
      class="flex-1 flex justify-center align-center gap-1 py-3 bg-primary text-white font-extrabold rounded-r-xl"
    >
      {{ includedRatio }}% <span class="text-sm">({{ row.count - (row.excluded.total || 0) }})
      </span>
    </span>
    <span
      v-else
      class="flex-1 flex justify-center align-center gap-1 py-3 bg-primary text-white font-extrabold rounded-r-xl"
    >
      <span class="text-sm">{{ row.count - (row.excluded.total || 0) }}</span>
    </span>
  </div>
</template>

<script lang="ts" setup>
import {computed} from "vue";
import {SupportProgramGroup, SupportProgramSettings} from "@/program/interfaces/Program";
import {Checkbox} from "@/core/components/ui/checkbox";
import SupportProgramSettingsService from "@/program/services/SupportProgramSettingsService";

const {row, hasExcluded, slug, settings} = defineProps<{
    row: SupportProgramGroup;
    hasExcluded: boolean;
    settings: SupportProgramSettings;
    slug: string
}>();

const emits = defineEmits<{
    (e: 'update:enabled', slug: string, enabled: boolean): void
}>();

/**
 * Determine if a group is enabled.
 *
 * If the group is not optional, it is always enabled.
 * If it is optional, check the settings to see if it is enabled.
 */
const isEnabled = computed(() => {
    if(!row.optional) {
        return true;
    }

    return SupportProgramSettingsService.isInclusionGroupEnabled(settings, slug);
});

/**
 * Update enabled state of a group.
 */
const updateEnabled = async (enabled: boolean | "indeterminate") => {
    if(enabled === 'indeterminate') {
        return;
    }

    emits("update:enabled", slug, enabled);
};

const excludedRatio = computed(() => {
    return row.count ? Math.round(row.excluded.total * 100 / row.count) : 0;
});

const includedRatio = computed(() => {
    return row.count ? Math.round((row.count - row.excluded.total) * 100 / row.count) : 0;
});
</script>
